import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import type { CreateWishlistResponse } from "@wishlist-hub/shared";
import { Button, Card, ErrorText, Input, Label, TextArea } from "../components/ui";
import { api } from "../services/api";

const schema = z.object({
  title: z.string().min(1).max(100),
  ownerName: z.string().min(1).max(50),
  description: z.string().max(500).optional(),
  isPrivate: z.boolean(),
});

type Form = z.infer<typeof schema>;

export function CreateWishlist() {
  const nav = useNavigate();
  const [serverError, setServerError] = useState("");
  const [created, setCreated] = useState<CreateWishlistResponse | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { isPrivate: false },
  });

  const onSubmit = async (data: Form) => {
    setServerError("");
    try {
      const { data: res } = await api.post<CreateWishlistResponse>("/wishlists", {
        title: data.title,
        ownerName: data.ownerName,
        description: data.description || undefined,
        isPrivate: data.isPrivate,
      });
      setCreated(res);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "response" in e
          ? String((e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message)
          : "Ошибка";
      setServerError(msg || "Не удалось создать список");
    }
  };

  if (created) {
    return (
      <Card>
        <h2 className="text-xl font-semibold text-white">Список создан</h2>
        <p className="mt-2 text-sm text-slate-400">Сохраните админ-ссылку — по ней можно редактировать список.</p>
        <dl className="mt-4 space-y-2 text-sm">
          <dt className="text-slate-500">Публичная</dt>
          <dd>
            <a href={created.publicUrl} className="text-violet-400 break-all">
              {created.publicUrl}
            </a>
          </dd>
          <dt className="text-slate-500">Админ</dt>
          <dd>
            <a href={created.adminUrl} className="text-amber-400 break-all">
              {created.adminUrl}
            </a>
          </dd>
        </dl>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={() => nav(`/admin/${created.adminToken}`)}>Открыть админку</Button>
          <Button variant="secondary" onClick={() => nav(`/wishlist/${created.slug}`)}>
            Публичный вид
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Helmet>
        <title>Новый список — WishList Hub</title>
      </Helmet>
      <Card className="max-w-lg mx-auto">
        <h1 className="text-2xl font-bold text-white">Новый список желаний</h1>
        <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Label>Название</Label>
            <Input {...register("title")} placeholder="День рождения" />
            {errors.title && <ErrorText>{errors.title.message}</ErrorText>}
          </div>
          <div>
            <Label>Ваше имя</Label>
            <Input {...register("ownerName")} placeholder="Как к вам обращаться" />
            {errors.ownerName && <ErrorText>{errors.ownerName.message}</ErrorText>}
          </div>
          <div>
            <Label>Описание (необязательно)</Label>
            <TextArea rows={3} {...register("description")} />
            {errors.description && <ErrorText>{errors.description.message}</ErrorText>}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" {...register("isPrivate")} className="rounded border-slate-600" />
            Режим «сюрприз» (скрывать забронированные подарки)
          </label>
          {serverError && <ErrorText>{serverError}</ErrorText>}
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? "Создание…" : "Создать"}
          </Button>
        </form>
      </Card>
    </>
  );
}
